import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useMemberAuth } from '../../context/MemberAuthContext';
import LogoImage from '../ui/LogoImage';

const mainLinks = [
  { to: '/',           label: 'Home'      },
  { to: '/events',     label: 'Events'    },
  { to: '/quotes',     label: 'Murshid'   },
  { to: '/islamiyah',  label: 'Islamiyah' },
  { to: '/library',    label: 'Library'   },
  { to: '/gallery',    label: 'Gallery'   },
  { to: '/sawia',      label: 'Sawia'     },
  { to: '/radio',      label: 'Radio'     },
  { to: '/contact',    label: 'Contact'   },
  { to: '/about',      label: 'About'     },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const memberCtx = useMemberAuth();
  const member = memberCtx?.member || null;
  const close = () => setOpen(false);

  return (
    <nav className="sticky top-0 z-50 bg-navy-700 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <LogoImage size={40} className="rounded-full border-2 border-cyan-400" />
            <div className="hidden sm:block">
              <p className="text-white font-bold text-sm leading-tight">JNF</p>
              <p className="text-cyan-400 text-xs font-medium leading-tight">Tijaniyyah</p>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-0.5">
            {mainLinks.map(({ to, label }) => (
              <NavLink key={to} to={to} end={to === '/'}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                    isActive ? 'bg-cyan-400 text-navy-900' : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </div>

          {/* Right CTAs */}
          <div className="hidden lg:flex items-center gap-2">
            {member ? (
              <Link to="/member/dashboard"
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all">
                <div className="h-6 w-6 rounded-full border border-cyan-400 overflow-hidden flex-shrink-0 bg-navy-600">
                  {member.profile_photo
                    ? <img src={member.profile_photo} alt="" className="w-full h-full object-cover" />
                    : <span className="flex items-center justify-center h-full text-[10px] text-white">👤</span>
                  }
                </div>
                My Portal
              </Link>
            ) : (
              <>
                <Link to="/member/register"
                  className="text-white/80 hover:text-white text-sm font-medium px-3 py-2 rounded-lg hover:bg-white/10 transition-all border border-white/20 hover:border-white/40">
                  Join Us
                </Link>
                <Link to="/member/login"
                  className="text-white/80 hover:text-white text-sm font-medium px-3 py-2 rounded-lg hover:bg-white/10 transition-all">
                  Member Login
                </Link>
              </>
            )}
          </div>

          {/* Mobile burger */}
          <button onClick={() => setOpen(!open)} className="lg:hidden text-white p-2 rounded-lg hover:bg-white/10">
            {open ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-white/10 bg-navy-800">
          <div className="px-4 py-3 space-y-1">
            {mainLinks.map(({ to, label }) => (
              <NavLink key={to} to={to} onClick={close} end={to === '/'}
                className={({ isActive }) =>
                  `block px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive ? 'bg-cyan-400 text-navy-900' : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
            <div className="pt-3 border-t border-white/10 mt-1 space-y-1.5">
              {member ? (
                <Link to="/member/dashboard" onClick={close}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-white/10 text-white">
                  👤 My Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/member/register" onClick={close}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-navy-600 text-white border border-white/20">
                    ✍️ Join JNF — Register
                  </Link>
                  <Link to="/member/login" onClick={close}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white/80 hover:text-white hover:bg-white/10">
                    🔑 Member Login
                  </Link>
                  <Link to="/member/forgot-password" onClick={close}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs text-white/50 hover:text-white/80">
                    Forgot Password?
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
